import { captureException } from '@/utils/sentry';
import { gql } from '@apollo/client';
import { isMatchingFn, isNotTagVariantPack } from '@/utils/scanning';
import {
  ItemsContext_VariantsPackFieldsFragment,
  ScannerScreen_SearchQuery,
  ScannerScreen_SearchQueryVariables,
} from '@/types';
import { OnDeviceScanningSearchType } from '@/contexts/dynamic-config';
import { PackUnit } from '@/enums';
import { ScanSound } from '@/components/scanner/scanner-types';
import { selectLowestPack } from '@/utils/pack-sort';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback, useMemo, useState } from 'react';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import { useItemQueryVariables } from './use-item-query-variables';
import { useLazyQuery } from './use-lazy-query';
import { usePlayScanSounds } from './use-play-scan-sounds';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

interface OnDeviceSearchResult {
  codeMatches: ItemsContext_VariantsPackFieldsFragment[];
  nameMatches: ItemsContext_VariantsPackFieldsFragment[];
}

export const SEARCH_BY_UPC_QUERY = gql`
  query ScannerScreen_Search(
    $code: String!
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    searchVariantPacksByUpc(
      query: $code
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }
  }
  ${VARIANTS_PACK_FRAGMENT}
`;

export const useScannerSearch = () => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const itemQueryVariables = useItemQueryVariables();
  const playScanSound = usePlayScanSounds();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const onDeviceScanningConfig = useDynamicConfigValue(
    'on_device_scanning_config',
  );

  const [isOnDeviceSearchInProgress, setIsOnDeviceSearchInProgress] =
    useState(false);
  const [isOffDeviceSearchInProgress, setIsOffDeviceSearchInProgress] =
    useState(false);
  const [offDeviceSearchResults, setOffDeviceSearchResults] = useState<
    ItemsContext_VariantsPackFieldsFragment[] | null
  >(null);
  const [onDeviceSearchResults, setOnDeviceSearchResults] = useState<
    ItemsContext_VariantsPackFieldsFragment[] | null
  >(null);

  const clearResults = useCallback(() => {
    setOnDeviceSearchResults(null);
    setOffDeviceSearchResults(null);
    setIsOnDeviceSearchInProgress(false);
    setIsOffDeviceSearchInProgress(false);
  }, []);

  const searchVariantPacksByUPC = useLazyQuery<
    ScannerScreen_SearchQuery,
    ScannerScreen_SearchQueryVariables
  >(SEARCH_BY_UPC_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'no-cache',
  });

  const searchOnDevice = useCallback(
    (
      code: string,
      variantPacks: ItemsContext_VariantsPackFieldsFragment[],
      skipPlaySound = false,
      // Lowest-UOM auto-select is an order-scan convenience: other callers
      // of this shared helper (credit requests, tag printing) must keep
      // showing a chooser on a multi-match rather than silently committing
      // to a pack.
      skipAutoSelectLowestUom = false,
    ) => {
      setIsOnDeviceSearchInProgress(true);

      const defaultMatchingPackUnit =
        onDeviceScanningConfig?.default_matching_pack_unit;
      const searchType =
        onDeviceScanningConfig?.search_type ??
        OnDeviceScanningSearchType.EXACT_MATCH;

      const { codeMatches, nameMatches } =
        variantPacks.reduce<OnDeviceSearchResult>(
          (acc, variantPack) => {
            if (!isNotTagVariantPack(variantPack)) {
              return acc;
            }

            const { codeMatch, nameMatch } = isMatchingFn(
              code,
              searchType,
              adaptVariantPackToItemListOption(variantPack),
            );

            if (codeMatch) {
              acc.codeMatches.push(variantPack);
            }
            if (nameMatch) {
              acc.nameMatches.push(variantPack);
            }
            return acc;
          },
          { codeMatches: [], nameMatches: [] },
        );

      // Always return single code match which indicates match intent
      let onDeviceSearchResults =
        codeMatches.length === 1
          ? codeMatches
          : [...codeMatches, ...nameMatches];

      // Attempt to find a single default matching pack unit when exact match
      // is enabled and there are multiple results
      if (
        onDeviceSearchResults.length > 1 &&
        defaultMatchingPackUnit &&
        searchType === OnDeviceScanningSearchType.EXACT_MATCH
      ) {
        const packMatchingResults = onDeviceSearchResults.filter(
          (vp) => vp.pack.unit === defaultMatchingPackUnit,
        );

        if (packMatchingResults.length === 1) {
          onDeviceSearchResults = packMatchingResults;
        }
      }

      // Attempt to auto-select the lowest-UOM pack when still multi-matched
      if (
        onDeviceSearchResults.length > 1 &&
        onDeviceScanningConfig?.auto_select_lowest_uom &&
        searchType === OnDeviceScanningSearchType.EXACT_MATCH &&
        !skipAutoSelectLowestUom
      ) {
        const lowestPack = selectLowestPack(onDeviceSearchResults, (vp) => ({
          displayString: vp.item.display_name,
          id: vp.uuid,
          unit: vp.pack.unit as PackUnit,
          unitCount: vp.pack.unit_count,
        }));

        if (lowestPack) {
          onDeviceSearchResults = [lowestPack];
        }
      }

      if (onDeviceSearchResults.length > 1 && !skipPlaySound) {
        playScanSound(ScanSound.MultipleResults);
      }

      setIsOnDeviceSearchInProgress(false);
      setOnDeviceSearchResults(onDeviceSearchResults);

      return onDeviceSearchResults;
    },
    [adaptVariantPackToItemListOption, onDeviceScanningConfig, playScanSound],
  );

  const searchOffDevice = useCallback(
    async (
      code: string,
      itemFilterFn?: (item: ItemsContext_VariantsPackFieldsFragment) => boolean,
      // Lowest-UOM auto-select is an order-scan convenience: other callers
      // of this shared helper (credit requests, tag printing) must keep
      // showing a chooser on a multi-match rather than silently committing
      // to a pack.
      skipAutoSelectLowestUom = false,
    ) => {
      try {
        setIsOffDeviceSearchInProgress(true);
        const { data: offDeviceSearchData } = await searchVariantPacksByUPC({
          variables: {
            ...itemQueryVariables,
            code,
            restaurantUUID,
            supplierUUID,
          },
        });

        let offDeviceSearchResults = (
          offDeviceSearchData?.searchVariantPacksByUpc || []
        ).reduce<ItemsContext_VariantsPackFieldsFragment[]>((acc, data) => {
          const variantPack = data.variant_pack!;
          if (!itemFilterFn || itemFilterFn(variantPack)) {
            acc.push(variantPack);
          }

          return acc;
        }, []);

        // Attempt to auto-select the lowest-UOM pack when still multi-matched.
        // The backend's own literal default_matching_pack_unit filter already
        // ran server-side; this only kicks in if that still left more than
        // one candidate, mirroring searchOnDevice's fallback. A code can match
        // packs of different items, so only collapse when every candidate is
        // a pack of the same item; otherwise the operator must pick.
        if (
          offDeviceSearchResults.length > 1 &&
          offDeviceSearchResults.every(
            (vp) => vp.item.uuid === offDeviceSearchResults[0].item.uuid,
          ) &&
          onDeviceScanningConfig?.auto_select_lowest_uom &&
          (onDeviceScanningConfig?.search_type ??
            OnDeviceScanningSearchType.EXACT_MATCH) ===
            OnDeviceScanningSearchType.EXACT_MATCH &&
          !skipAutoSelectLowestUom
        ) {
          const lowestPack = selectLowestPack(offDeviceSearchResults, (vp) => ({
            displayString: vp.item.display_name,
            id: vp.uuid,
            unit: vp.pack.unit as PackUnit,
            unitCount: vp.pack.unit_count,
          }));

          if (lowestPack) {
            offDeviceSearchResults = [lowestPack];
          }
        }

        if (offDeviceSearchResults.length === 0) {
          playScanSound(ScanSound.NoResults);
        } else if (offDeviceSearchResults.length > 1) {
          playScanSound(ScanSound.MultipleResults);
        }

        setIsOffDeviceSearchInProgress(false);
        setOffDeviceSearchResults(offDeviceSearchResults);

        return offDeviceSearchResults;
      } catch (e) {
        setIsOffDeviceSearchInProgress(false);
        captureException(e);
        return [];
      }
    },
    [
      itemQueryVariables,
      onDeviceScanningConfig,
      playScanSound,
      searchVariantPacksByUPC,
      restaurantUUID,
      supplierUUID,
    ],
  );

  return useMemo(
    () => ({
      clearResults,
      isOffDeviceSearchInProgress,
      isOnDeviceSearchInProgress,
      offDeviceSearchResults,
      onDeviceSearchResults,
      searchOffDevice,
      searchOnDevice,
    }),
    [
      clearResults,
      isOffDeviceSearchInProgress,
      isOnDeviceSearchInProgress,
      offDeviceSearchResults,
      onDeviceSearchResults,
      searchOffDevice,
      searchOnDevice,
    ],
  );
};
